import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ModalComponent } from '../modal/modal.component';
import { ModalSize } from '../modal/modal-size.enum';
import { PatientService } from '../../core/services/patient.service';
import { AlertService } from '../alert/alert.service';
import { SelectOption } from '../../core/models/select-option.model';
import { PatientMergeFormModel } from './patient-merge-form.model';
import { DirectMergePatientRequest } from '../../core/models/direct-merge-patient-request.model';

@Component({
  selector: 'app-patient-merge-modal',
  templateUrl: './patient-merge-modal.component.html',
  styleUrl: './patient-merge-modal.component.scss'
})
export class PatientMergeModalComponent implements OnInit {
  @ViewChild('modal') modal: ModalComponent;

  @Output() mergeCompleted = new EventEmitter<string>();

  readonly ModalSize = ModalSize;

  form: FormGroup<PatientMergeFormModel>;
  currentStep: 1 | 2 | 3 = 1;
  sourcePatientInfo: any = null;
  targetPatientInfo: any = null;
  loadingStep = false;

  private _pendingSourceOption: SelectOption | null = null;
  private _pendingTargetOption: SelectOption | null = null;

  constructor(
    private _patientService: PatientService,
    private _alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup<PatientMergeFormModel>({
      sourcePatientId: new UntypedFormControl('', Validators.required),
      targetPatientId: new UntypedFormControl('', Validators.required),
      processing: new UntypedFormControl(false)
    });
  }

  public show(): void {
    this.currentStep = 1;
    this.sourcePatientInfo = null;
    this.targetPatientInfo = null;
    this.loadingStep = false;
    this._pendingSourceOption = null;
    this._pendingTargetOption = null;
    this.form.reset({ sourcePatientId: '', targetPatientId: '', processing: false });
    this.modal.show();
  }

  get canProceedStep1(): boolean {
    return this._pendingSourceOption != null && !this.loadingStep;
  }

  get canProceedStep2(): boolean {
    return this._pendingTargetOption != null && !this.loadingStep;
  }

  hideModal(): void {
    this.modal.hide();
  }

  onBack(): void {
    if (this.currentStep === 3)
    {
      this.currentStep = 2;
      this.targetPatientInfo = null;
      this._pendingTargetOption = null;
      this.form.patchValue({ targetPatientId: '', processing: false });
    }
    else if (this.currentStep === 2)
    {
      this.currentStep = 1;
      this.sourcePatientInfo = null;
      this._pendingSourceOption = null;
      this.form.patchValue({ sourcePatientId: '', processing: false });
    }
  }

  onSourcePatientSelected(option: SelectOption): void {
    this._pendingSourceOption = option;
  }

  onPatientSelected(option: SelectOption): void {
    this._pendingTargetOption = option;
  }

  onNext(): void {
    if (this.currentStep === 1)
    {
      if (!this._pendingSourceOption) { return; }
      this.loadingStep = true;
      this._patientService.getPatientMergeInfo(this._pendingSourceOption.value as string).subscribe({
        next: (info) => {
          this.sourcePatientInfo = info;
          this.currentStep = 2;
          this.loadingStep = false;
        },
        error: () => {
          this._alertService.showErrorAlert('Failed to load source patient details.');
          this.loadingStep = false;
        }
      });
    }
    else if (this.currentStep === 2)
    {
      if (!this._pendingTargetOption) { return; }
      this.loadingStep = true;
      this._patientService.getPatientMergeInfo(this._pendingTargetOption.value as string).subscribe({
        next: (info) => {
          this.targetPatientInfo = info;
          this.currentStep = 3;
          this.loadingStep = false;
        },
        error: () => {
          this._alertService.showErrorAlert('Failed to load patient details.');
          this.loadingStep = false;
        }
      });
    }
  }

  onConfirmMerge(): void {
    if (this.form.invalid || !this.sourcePatientInfo || !this.targetPatientInfo) {
      return;
    }

    this.form.patchValue({ processing: true });

    const request: DirectMergePatientRequest = {
      targetPatientId: this.targetPatientInfo.id
    };

    this._patientService.directMergePatient(this.sourcePatientInfo.id, request).subscribe({
      next: () => {
        this.modal.hide();
        this.mergeCompleted.emit(this.targetPatientInfo.patientTrialId);
      },
      error: (err) => {
        this._alertService.showErrorAlert(err);
        this.form.patchValue({ processing: false });
      }
    });
  }
}
